import { supabase } from './supabase';
import { Message, SenderRole } from '@/types/messages';
import { RealtimeChannel } from '@supabase/supabase-js';

export async function fetchMessages(userId: string): Promise<{ data: Message[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Failed to fetch messages') };
  }
}

export async function sendMessage(
  userId: string,
  senderRole: SenderRole,
  messageText: string
): Promise<{ data: Message | null; error: Error | null }> {
  try {
    if (!messageText.trim()) {
      return { data: null, error: new Error('Message text cannot be empty') };
    }

    const { data, error } = await supabase
      .from('messages')
      .insert({
        user_id: userId,
        sender_role: senderRole,
        message_text: messageText,
      })
      .select()
      .single();

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Failed to send message') };
  }
}

export function subscribeToMessages(
  userId: string,
  onMessage: (message: Message) => void
): RealtimeChannel {
  const channel = supabase
    .channel(`messages:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        onMessage(payload.new as Message);
      }
    )
    .subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        console.log('Successfully subscribed to messages');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('Error subscribing to messages:', err);
      } else if (status === 'TIMED_OUT') {
        console.error('Subscription timed out');
      }
    });

  return channel;
}

export async function unsubscribeFromMessages(channel: RealtimeChannel): Promise<void> {
  await supabase.removeChannel(channel);
}

export async function fetchUsersWithMessages(): Promise<{ data: string[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('user_id')
      .order('created_at', { ascending: false });

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    // Get unique user IDs
    const uniqueUserIds = [...new Set(data.map(msg => msg.user_id))];
    return { data: uniqueUserIds, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Failed to fetch users') };
  }
}

export interface ConversationSummary {
  userId: string;
  lastMessage: string;
  lastMessageAt: string;
  lastSenderRole: SenderRole;
  unreadCount: number;
}

/**
 * Builds a per-user summary of every conversation the admin has: the most
 * recent message, its timestamp, and how many incoming (non-admin) messages
 * are still unread. Used to power the conversation list ordering and badges.
 */
export async function fetchConversationSummaries(): Promise<{ data: ConversationSummary[] | null; error: Error | null }> {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('user_id, sender_role, message_text, created_at, is_read')
      .order('created_at', { ascending: false });

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    const summaries = new Map<string, ConversationSummary>();
    for (const msg of data as Message[]) {
      let summary = summaries.get(msg.user_id);
      if (!summary) {
        // First (most recent) message we see for this user.
        summary = {
          userId: msg.user_id,
          lastMessage: msg.message_text,
          lastMessageAt: msg.created_at,
          lastSenderRole: msg.sender_role,
          unreadCount: 0,
        };
        summaries.set(msg.user_id, summary);
      }
      // Incoming messages (from a client or editor) that are still unread.
      if (msg.sender_role !== 'admin' && !msg.is_read) {
        summary.unreadCount += 1;
      }
    }

    return { data: Array.from(summaries.values()), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err : new Error('Failed to fetch conversation summaries') };
  }
}

/**
 * Marks every incoming (non-admin) message in a conversation as read. Called
 * when the admin opens a conversation so the unread badge clears.
 */
export async function markConversationAsRead(userId: string): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('user_id', userId)
      .neq('sender_role', 'admin')
      .eq('is_read', false);

    if (error) {
      return { error: new Error(error.message) };
    }

    return { error: null };
  } catch (err) {
    return { error: err instanceof Error ? err : new Error('Failed to mark conversation as read') };
  }
}

/**
 * Counts unread messages from the admin/support for a specific client or
 * editor (their inbox). Used to badge the "Messages" link in their sidebar.
 */
export async function fetchInboxUnreadCount(userId: string): Promise<{ count: number; error: Error | null }> {
  try {
    const { count, error } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('sender_role', 'admin')
      .eq('is_read', false);

    if (error) {
      return { count: 0, error: new Error(error.message) };
    }

    return { count: count || 0, error: null };
  } catch (err) {
    return { count: 0, error: err instanceof Error ? err : new Error('Failed to count unread messages') };
  }
}

/**
 * Marks all admin/support messages in a user's inbox as read. Called when the
 * client or editor opens their messages page so the sidebar badge clears.
 */
export async function markInboxAsRead(userId: string): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('sender_role', 'admin')
      .eq('is_read', false);

    if (error) {
      return { error: new Error(error.message) };
    }

    return { error: null };
  } catch (err) {
    return { error: err instanceof Error ? err : new Error('Failed to mark inbox as read') };
  }
}

/**
 * Subscribes to all new incoming messages across every conversation so the
 * admin conversation list can update unread counts live.
 */
export function subscribeToAllMessages(
  onMessage: (message: Message) => void
): RealtimeChannel {
  const channel = supabase
    .channel('messages:all')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'messages' },
      (payload) => onMessage(payload.new as Message)
    )
    .subscribe();

  return channel;
}

export async function deleteMessage(messageId: string): Promise<{ error: Error | null }> {
  try {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId);

    if (error) {
      return { error: new Error(error.message) };
    }

    return { error: null };
  } catch (err) {
    return { error: err instanceof Error ? err : new Error('Failed to delete message') };
  }
}
